import { FC } from "react";
import { Link } from "react-router";

import {
  format,
  getDaysInMonth,
  setDate,
  startOfMonth,
  isSameDay,
  getDay,
  subDays
} from "date-fns";
import { ICalendarProps } from "./types";
import classNames from "classnames";
import { EColorRGB } from "../../constants/colors";
import { ScoreDailyAverage } from "../../types/scores";

import styles from "./Calendar.module.css";

type NormalizedDay = {
  created_at: string;
  value: number | null;
  date: Date;
  isPadding: boolean;
};

const Calendar: FC<ICalendarProps> = ({ data, linkFormat, currentBrandId = '', hashId }) => {
  const normalizeMonthData = (monthData: ScoreDailyAverage[], monthDate: Date): NormalizedDay[] => {
    const daysInMonth = getDaysInMonth(monthDate);
    const normalizedData: NormalizedDay[] = [];
    const firstDayOfMonth = startOfMonth(monthDate);
    let dayOfWeek = getDay(firstDayOfMonth);
    dayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    const paddingDays = dayOfWeek - 1;

    for (let i = paddingDays - 1; i >= 0; i--) {
      const paddingDate = subDays(firstDayOfMonth, i + 1);
      normalizedData.push({
        created_at: format(paddingDate, 'yyyy-MM-dd'),
        value: null,
        date: paddingDate,
        isPadding: true
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = setDate(monthDate, day);
      const existingData = monthData.find(item =>
        isSameDay(new Date(item.created_at), currentDate)
      );

      if (existingData) {
        normalizedData.push({
          created_at: existingData.created_at,
          value: existingData.visibility,
          date: currentDate,
          isPadding: false
        });
      } else {
        normalizedData.push({
          created_at: format(currentDate, 'yyyy-MM-dd'),
          value: null,
          date: currentDate,
          isPadding: false
        });
      }
    }

    return normalizedData;
  };

  type GroupedData = Record<string, NormalizedDay[]>;

  const groupDataByMonth = (data: ScoreDailyAverage[]): GroupedData => {
    const grouped: Record<string, ScoreDailyAverage[]> = data.reduce((acc: Record<string, ScoreDailyAverage[]>, item: ScoreDailyAverage) => {
      const date = new Date(item.created_at);
      const monthKey = format(date, 'MMMM yyyy');

      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }

      acc[monthKey].push(item);
      return acc;
    }, {});

    const result: GroupedData = {};
    Object.keys(grouped).forEach(monthKey => {
      const monthDate = new Date(grouped[monthKey][0].created_at);
      result[monthKey] = normalizeMonthData(grouped[monthKey], startOfMonth(monthDate));
    });

    return result;
  };

  const monthlyData: GroupedData = groupDataByMonth(data);

  interface ConditionalLinkProps extends React.HTMLAttributes<HTMLDivElement | HTMLAnchorElement> {
    value: number | null;
    children: React.ReactNode;
    to?: string;
  }

  const ConditionalLink: FC<ConditionalLinkProps> = ({ value, children, to, ...props }) => {
    return value !== null && to ?
      <Link to={to} {...props}>{children}</Link> :
      <div {...props}>{children}</div>;
  };

  return (
    <div className={styles["calendar-container"]}>
      {Object.entries(monthlyData).map(([month, monthData], index) => (
        <div key={month} className={styles["calendar-month"]}>
          {index > 0 && <h3 className={styles["calendar-month__title"]}>{month}</h3>}
          <div className={styles["calendar"]}>
            {monthData.map((item, index: number) => {
              const opacity = item.value ? item.value / 100 : 0;
              const date = format(item.date, 'yyyy-MM-dd');

              return (
                <ConditionalLink
                  value={item.value}
                  to={linkFormat ? linkFormat.replace(':id', currentBrandId).replace(':date', date) + (hashId ? `#${hashId}` : '') : ''}
                  style={item.value ? {
                    backgroundColor: `rgba(${EColorRGB.Green}, ${opacity})`,
                  } : undefined}
                  key={index}
                  data-value={item.value}
                  className={classNames(
                    styles["calendar__day"],
                    !item.value && styles["is-empty"],
                    item.isPadding && styles["is-padding"]
                  )}>
                  <p>{item.value}</p>
                </ConditionalLink>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Calendar;
